import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import {
  AlertCircle, ArrowLeft, ArrowRight, BookOpen,
  FileText, Loader2, Minus, Plus, RotateCcw, X,
} from "lucide-react";
import { useActiveCohort, withCohort } from "@/lib/cohort-context";
import { ResearchCover, ResearchExplainer, ResearchFeedbackPanel, ResearchPdfNote } from "@/components/research/research-editorial";

const BASE = import.meta.env.BASE_URL;

export interface ResearchIndexItem {
  id: number;
  slug: string;
  companyName: string;
  publishedAt: string | null;
  sector: string | null;
  geography: string | null;
  cardSnippet: string | null;
  evaluationStatus: string | null;
  estimatedReadMinutes: number | null;
  entitlementCohortId: number;
  disclosureAccepted: boolean;
  readingStatus: "new" | "in_progress" | "completed";
  readingProgressPercent: number;
}

export interface ResearchPim {
  id: number;
  companyName: string;
  slug: string;
  status: string;
  publishedAt: string | null;
}

export interface ResearchVersion {
  id: number; pimId: number; version: number; pageCount: number;
  sector: string | null; geography: string | null; stage: string | null; leadAdvisor: string | null;
  preparedDate: string | null; evaluationStatus: string; disclosureVersion: string; cardSnippet: string | null;
  executiveSummary?: string | null; evaluationThesis?: string | null; researchThemes?: unknown[];
  claims?: unknown[]; evidence?: unknown[]; evidenceGaps?: unknown[]; risks?: unknown[]; sources?: unknown[];
  estimatedReadMinutes: number | null;
}

export interface ResearchChapter {
  key: string; title: string; body: string;
}

export interface ResearchNarrative {
  pim: ResearchPim;
  version: ResearchVersion;
  entitlementCohortId: number;
  disclosureAccepted: boolean;
  deeperContentAvailable: boolean;
}

export interface ResearchIndexEnvelope {
  data: ResearchIndexItem[];
  meta?: { activeCohortId: number | null };
}

export interface ResearchDetailEnvelope {
  data: ResearchNarrative;
}

export interface ResearchIndexResponse {
  items: ResearchIndexItem[];
  activeCohortId: number | null;
}

export function unwrapResearchIndex(envelope: ResearchIndexEnvelope): ResearchIndexResponse {
  return { items: Array.isArray(envelope.data) ? envelope.data : [], activeCohortId: envelope.meta?.activeCohortId ?? null };
}

export function unwrapResearchNarrative(envelope: ResearchDetailEnvelope): ResearchNarrative {
  return envelope.data;
}

function contentText(value: unknown): string {
  if (typeof value === "string") return value;
  if (!value || typeof value !== "object") return String(value ?? "");
  const item = value as Record<string, unknown>;
  const main = [item.body, item.text, item.claim, item.description, item.title, item.label].find(part => typeof part === "string");
  const pages = Array.isArray(item.pageNumbers) && item.pageNumbers.length ? ` (PIM p. ${item.pageNumbers.join(", ")})` : "";
  return `${typeof main === "string" ? main : JSON.stringify(value)}${pages}`;
}

export function narrativeChapters(version: ResearchVersion): ResearchChapter[] {
  const block = (value: unknown[] | string | null | undefined) =>
    Array.isArray(value) ? value.map(contentText).filter(Boolean).join("\n\n") : typeof value === "string" ? value : "";
  return [
    { key: "pipeline_reason", title: "Why this entered the pipeline", body: block(version.evaluationThesis || version.researchThemes) },
    { key: "company_claims", title: "What the company claims", body: block(version.claims) },
    { key: "evidence_reviewed", title: "What we have seen", body: block(version.evidence) },
    { key: "evidence_gaps", title: "What remains unproven", body: block(version.evidenceGaps) },
    { key: "risks", title: "What could break the case", body: block(version.risks) },
    { key: "sources", title: "Sources and further reading", body: block(version.sources) },
  ];
}

/* Presentation-only segmentation: preserves every character of source text.
   Splits on existing line breaks; very long single blocks are grouped by
   sentence boundaries (about three sentences each). Nothing is added. */
export function splitParagraphs(text: string): string[] {
  const blocks = text.split(/\n\s*\n|\r?\n/).map(part => part.trim()).filter(Boolean);
  const out: string[] = [];
  for (const block of blocks) {
    if (block.length < 520) { out.push(block); continue; }
    const sentences = block.split(/(?<=[.!?])\s+(?=[A-Z0-9"“(])/);
    for (let i = 0; i < sentences.length; i += 3) out.push(sentences.slice(i, i + 3).join(" "));
  }
  return out;
}


/* The backend intentionally omits deeper version fields until disclosure acceptance. */
export interface ResearchNarrativeResponse {
  data: ResearchNarrative;
}

function authHeaders(extra?: HeadersInit): HeadersInit {
  return { "x-investor-token": localStorage.getItem("investor_token") || "", ...extra };
}

function formatDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

async function errorMessage(response: Response, fallback = "Research could not be loaded. Please try again.") {
  try {
    const payload = await response.json() as { error?: { message?: string } };
    if (payload.error?.message) return payload.error.message;
  } catch {
    // A document response or an intermediary may not return JSON.
  }
  if (response.status === 401) return "Your session has ended. Please sign in again.";
  if (response.status === 403 || response.status === 404) return "This research is not available in your active cohort.";
  return fallback;
}

function Status({ children }: { children: React.ReactNode }) {
  return (
    <div role="status" data-testid="status-research-loading" className="flex items-center justify-center gap-3 py-24 text-white/50">
      <Loader2 className="h-5 w-5 animate-spin text-[#FF5C00]" aria-hidden="true" />
      <span className="text-sm">{children}</span>
    </div>
  );
}

function Failure({ message, retry }: { message: string; retry: () => void }) {
  return (
    <div role="alert" data-testid="status-research-error" className="research-error-surface min-w-0 break-words rounded-lg border p-6">
      <div className="flex items-start gap-3">
        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
        <div>
          <p className="text-sm">{message}</p>
          <button data-testid="button-retry-research" onClick={retry} className="mt-3 text-xs font-semibold underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-current">
            Try again
          </button>
        </div>
      </div>
    </div>
  );
}

function ResearchIndexHeader({ activeCohortId }: { activeCohortId?: number | null }) {
  return (
    <header className="rl-scope rl-rule min-w-0 border-b pb-8">
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-start">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] rl-accent">Research Pipeline</p>
          <h1 className="mt-3 max-w-2xl font-serif text-4xl leading-none text-white md:text-6xl rl-ink">The working library.</h1>
          <p className="mt-5 max-w-2xl text-sm leading-6 rl-body md:text-base">
            A transparent view of the companies we are studying, the questions we are asking, and the evidence still missing.
          </p>
        </div>
        {activeCohortId !== undefined && (
          <span className="font-mono text-[10px] uppercase tracking-wider rl-muted">
            Active cohort · {activeCohortId ?? "All accessible"}
          </span>
        )}
      </div>
      <p className="mt-7 max-w-3xl rn-accent-rule rl-muted border-l-2 pl-4 text-xs leading-5" style={{ borderColor: "var(--rl-accent)" }}>
        Research is preliminary and for discussion and learning only. A company under evaluation may never be onboarded.
        This is not an offer, solicitation, investment recommendation, or investment advice.
      </p>
    </header>
  );
}

function ResearchStatePage({ children }: { children: React.ReactNode }) {
  return (
    <div className="animate-in fade-in duration-300" data-testid="research-state-page">
      <ResearchIndexHeader />
      <div className="py-8">{children}</div>
    </div>
  );
}

export default function InvestorResearchIndex() {
  const [, navigate] = useLocation();
  const { cohortId } = useActiveCohort();
  const [data, setData] = useState<ResearchIndexResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(withCohort(`${BASE}api/investor-portal/research`, cohortId), {
        headers: authHeaders(),
        credentials: "include",
      });
      if (!response.ok) throw new Error(await errorMessage(response));
      setData(unwrapResearchIndex(await response.json() as ResearchIndexEnvelope));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Research could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [cohortId]);

  useEffect(() => { void load(); }, [load]);

  if (loading) return <ResearchStatePage><Status>Opening the research library…</Status></ResearchStatePage>;
  if (error || !data) {
    return <ResearchStatePage><Failure message={error || "Research could not be loaded."} retry={load} /></ResearchStatePage>;
  }

  const items = data.items;
  const active = items.filter(item => (item.evaluationStatus || "").toLowerCase().includes("under"));
  const concluded = items.filter(item => !active.includes(item));
  const lead = active[0] || items[0];
  const remaining = items.filter(item => item.id !== lead?.id);

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-500" data-testid="research-index">
      <ResearchIndexHeader activeCohortId={data.activeCohortId} />

      {!lead ? (
        <section className="rl-scope py-20" data-testid="status-research-empty">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] rl-accent">Research in progress</p>
          <h2 className="mt-3 font-serif text-3xl rl-ink">The next memorandum is being prepared.</h2>
          <p className="mt-3 max-w-xl text-sm leading-6 rl-muted">
            Approved research explicitly made available to this cohort will appear here.
          </p>
        </section>
      ) : (
        <div className="rl-scope space-y-10 py-10">
          <article data-testid={`card-research-${lead.id}`} className="rl-card grid overflow-hidden rounded-2xl border md:grid-cols-[1.2fr_1fr]">
            <ResearchCover seed={lead.slug} companyName={lead.companyName} sector={lead.sector} size="hero" />
            <div className="flex min-w-0 flex-col p-6 md:p-9">
              <div className="flex flex-wrap justify-between gap-3 font-mono text-[10px] uppercase tracking-[0.16em]">
                <span className="rl-accent">Lead evaluation</span>
                <span className="rl-muted">{!lead.disclosureAccepted ? "New · " : ""}{lead.publishedAt ? formatDate(lead.publishedAt) : "Published"}</span>
              </div>
              <h2 className="rl-ink mt-6 break-words font-serif text-3xl md:text-4xl" data-testid={`text-research-company-${lead.id}`}>{lead.companyName}</h2>
              <p className="rl-muted mt-3 font-mono text-[10px] uppercase tracking-wider">
                {[lead.evaluationStatus, lead.sector, lead.geography, lead.estimatedReadMinutes ? `${lead.estimatedReadMinutes} min read` : null].filter(Boolean).join(" · ")}
              </p>
              <p className="rl-body mt-5 text-base leading-7">{lead.cardSnippet}</p>
              <div className="mt-auto flex flex-wrap items-center gap-4 pt-8">
                <button
                  data-testid={`button-open-research-${lead.id}`}
                  onClick={() => navigate(`/investor/portal/research/${lead.id}`)}
                  className="rn-cta inline-flex min-h-11 items-center gap-2 rounded-md px-5 text-sm font-semibold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C94A00]"
                >
                  Read research narrative <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </button>
                <ResearchPdfNote />
              </div>
            </div>
          </article>

          <ResearchExplainer />

          {remaining.length > 0 && (
            <div>
              <div className="rl-rule flex items-end justify-between border-b pb-3">
                <h2 className="rl-ink font-serif text-2xl">Published research</h2>
                <span className="rl-muted font-mono text-[10px] uppercase tracking-wider">{remaining.length} items</span>
              </div>
              <div className="mt-6 grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {remaining.map(item => (
                  <Link
                    key={item.id}
                    href={`/investor/portal/research/${item.id}`}
                    data-testid={`link-research-${item.id}`}
                    className="rl-card flex min-w-0 flex-col overflow-hidden rounded-2xl border focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C94A00]"
                  >
                    <ResearchCover seed={item.slug} companyName={item.companyName} sector={item.sector} />
                    <div className="flex flex-1 flex-col p-5">
                      <div className="flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-wider">
                        {!item.disclosureAccepted && <span className="rl-accent">New</span>}
                        <span className="rl-muted">{item.evaluationStatus}</span>
                      </div>
                      <h3 className="rl-ink mt-2 font-serif text-xl">{item.companyName}</h3>
                      <p className="rl-body mt-2 line-clamp-3 text-sm leading-6">{item.cardSnippet}</p>
                      <p className="rl-muted mt-auto pt-4 font-mono text-[10px] uppercase tracking-wider">
                        {[item.sector, item.geography, item.estimatedReadMinutes ? `${item.estimatedReadMinutes} min read` : null].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
          {concluded.length > 0 && (
            <p className="rl-muted text-xs">
              Concluded evaluations remain visible only where an approved historical note is available.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function InvestorResearchNarrative({ pimId }: { pimId: string }) {
  const [, navigate] = useLocation();
  const { cohortId } = useActiveCohort();
  const [data, setData] = useState<ResearchNarrative | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(withCohort(`${BASE}api/investor-portal/research/${pimId}`, cohortId), {
        headers: authHeaders(),
        credentials: "include",
      });
      if (!response.ok) throw new Error(await errorMessage(response));
      setData(unwrapResearchNarrative(await response.json() as ResearchDetailEnvelope));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Research could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [cohortId, pimId]);

  useEffect(() => { void load(); }, [load]);

  const acceptDisclosure = async () => {
    if (!data) return;
    setAccepting(true);
    setAcceptError(null);
    try {
      const response = await fetch(`${BASE}api/investor-portal/research/${pimId}/disclosure-acceptance`, {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        credentials: "include",
        body: JSON.stringify({ disclosureVersion: data.version.disclosureVersion }),
      });
      if (!response.ok) throw new Error(await errorMessage(response, "Your acknowledgement could not be recorded. Please try again."));
      await load();
    } catch (err) {
      setAcceptError(err instanceof Error ? err.message : "Your acknowledgement could not be recorded.");
    } finally {
      setAccepting(false);
    }
  };

  if (loading || error || !data) {
    return (
      <div className="animate-in fade-in duration-300" data-testid="research-narrative-state">
        <Link href="/investor/portal/research" data-testid="link-back-research" className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-white">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Research Pipeline
        </Link>
        <h1 className="mt-6 font-serif text-4xl text-white">Research Pipeline</h1>
        <div className="mt-6">
          {loading
            ? <Status>Opening the research narrative…</Status>
            : <Failure message={error || "Research could not be loaded."} retry={load} />}
        </div>
      </div>
    );
  }

  const { pim, version, disclosureAccepted } = data;
  const chapters = narrativeChapters(version);
  const summaryText = version.executiveSummary || version.cardSnippet || "Deeper narrative is available after acknowledging the current disclosure.";
  const summaryParagraphs = splitParagraphs(summaryText);
  const [leadParagraph, ...restParagraphs] = summaryParagraphs;
  return (
    <div className="rn-scope min-w-0 animate-in fade-in duration-500" data-testid="research-narrative">
      <Link href="/investor/portal/research" data-testid="link-back-research" className="rn-muted inline-flex items-center gap-2 py-1 text-sm font-medium hover:underline underline-offset-4">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Research Pipeline
      </Link>
      <div className="mt-8 grid min-w-0 gap-12 lg:grid-cols-[220px_minmax(0,1fr)]">
        <nav aria-label="Research chapters" className="hidden lg:block">
          <div className="sticky top-6">
            <p className="rn-muted mb-3 font-mono text-[11px] font-semibold uppercase tracking-[0.14em]">Chapters</p>
            {chapters.map((chapter, index) => (
              <a
                key={chapter.key}
                data-testid={`link-chapter-${chapter.key}`}
                href={`#chapter-${chapter.key}`}
                className="rn-nav-link flex gap-3 border-l-2 py-2.5 pl-3 text-[13px] leading-5 transition-colors"
              >
                <span className="font-mono text-[11px] leading-5">{String(index + 1).padStart(2, "0")}</span><span>{chapter.title}</span>
              </a>
            ))}
          </div>
        </nav>
        <main className="min-w-0">
          <p className="rn-accent font-mono text-[11px] font-semibold uppercase tracking-[0.14em]">{version.evaluationStatus} · PIM v{version.version}</p>
          <h1 className="rn-ink mt-3 break-words font-serif text-4xl leading-[1.05] md:text-6xl" data-testid="text-research-company">{pim.companyName}</h1>
          <p className="rn-muted mt-5 flex flex-wrap gap-x-5 gap-y-2 font-mono text-[11px] uppercase tracking-wider">
            <span>{[version.sector, version.geography].filter(Boolean).join(" · ")}</span>
            {version.preparedDate && <span>Prepared {formatDate(version.preparedDate)}</span>}
            {version.leadAdvisor && <span>Lead advisor: {version.leadAdvisor}</span>}
          </p>

          <section aria-labelledby="research-summary-title" className="rn-rule mt-10 max-w-[68ch] border-t pt-8" data-testid="text-research-summary">
            <h2 id="research-summary-title" className="rn-muted font-mono text-[11px] font-semibold uppercase tracking-[0.14em]">Executive summary</h2>
            {leadParagraph && <p className="rn-ink mt-4 font-serif text-xl leading-8 md:text-[22px] md:leading-9">{leadParagraph}</p>}
            {restParagraphs.length > 0 && (
              <div className="rn-prose rn-body mt-5 text-base leading-7 md:text-[17px] md:leading-8">
                {restParagraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
              </div>
            )}
          </section>

          <button
            data-testid="button-enter-reading-room"
            onClick={() => disclosureAccepted && navigate(`/investor/portal/research/${pim.id}/read`)}
            disabled={!disclosureAccepted}
            className="rn-cta mt-8 inline-flex min-h-11 items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C94A00] disabled:cursor-not-allowed"
          >
            <BookOpen className="h-4 w-4" aria-hidden="true" /> Enter Reading Room <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
          <p className="rn-muted rn-accent-rule mt-7 max-w-[68ch] border-l-2 pl-4 text-[13px] leading-6">
            Under evaluation — not a portfolio confirmation. Preliminary, private and confidential. This material may include
            company representations that have not been independently verified and is not an offer, solicitation, investment
            recommendation, or investment advice.
          </p>

          <div className="rl-scope mt-10 max-w-[68ch]"><ResearchExplainer compact /></div>

          <div className={`relative mt-14 ${disclosureAccepted ? "" : "max-h-[540px] overflow-hidden"}`}>
            <div aria-hidden={!disclosureAccepted}>
              {chapters.map((chapter, index) => (
                <section id={`chapter-${chapter.key}`} key={chapter.key} className="rn-rule scroll-mt-8 border-t py-10">
                  <p className="rn-accent font-mono text-[11px] font-semibold uppercase tracking-[0.14em]">Chapter {String(index + 1).padStart(2, "0")}</p>
                  <h2 className="rn-ink mt-2 font-serif text-2xl md:text-3xl">{chapter.title}</h2>
                  <div className="rn-prose rn-body mt-5 max-w-[68ch] break-words text-base leading-7">
                    {splitParagraphs(chapter.body).map((paragraph, i) => <p key={i}>{paragraph}</p>)}
                  </div>
                </section>
              ))}
            </div>
            {!disclosureAccepted && (
              <div className="rn-veil absolute inset-0 flex items-center justify-center p-4 backdrop-blur-md">
                <section role="dialog" aria-modal="true" aria-labelledby="disclosure-title" className="rn-panel max-w-xl border p-6 shadow-2xl md:p-8">
                  <p className="rn-accent font-mono text-[11px] font-semibold uppercase tracking-[0.14em]">Confidentiality acknowledgement · {version.disclosureVersion}</p>
                  <h2 id="disclosure-title" className="rn-ink mt-3 font-serif text-3xl">Before you continue</h2>
                  <p className="rn-body mt-4 text-sm leading-6">
                    I understand this preliminary material is private and confidential, must not be redistributed, may contain
                    company representations that have not been independently verified, and is provided only for discussion and
                    learning. It is not an offer, solicitation, investment recommendation, or investment advice. No representation
                    or warranty is made about completeness or accuracy.
                  </p>
                  {acceptError && <p role="alert" data-testid="status-disclosure-error" className="research-error-surface mt-4 rounded border p-3 text-xs">{acceptError}</p>}
                  <button
                    data-testid="button-accept-disclosure"
                    onClick={acceptDisclosure}
                    disabled={accepting}
                    className="rn-cta mt-6 inline-flex min-h-11 items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold"
                  >
                    {accepting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                    Acknowledge and continue
                  </button>
                </section>
              </div>
            )}
          </div>
          {disclosureAccepted && (
            <div className="rl-scope">
              <ResearchFeedbackPanel pimId={pim.id} />
            </div>
          )}
          {disclosureAccepted && (
            <div className="rn-panel sticky bottom-3 z-10 mx-auto flex w-fit max-w-full gap-2 rounded-md border p-2 shadow-xl lg:hidden">
              <label htmlFor="research-chapter-mobile" className="sr-only">Jump to chapter</label>
              <select
                id="research-chapter-mobile"
                data-testid="select-research-chapter"
                onChange={event => document.getElementById(`chapter-${event.target.value}`)?.scrollIntoView({ behavior: "smooth" })}
                className="rn-select max-w-full rounded px-3 py-2 text-sm"
              >
                {chapters.map(chapter => <option key={chapter.key} value={chapter.key}>{chapter.title}</option>)}
              </select>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export function InvestorResearchReadingRoom({ pimId }: { pimId: string }) {
  const [, navigate] = useLocation();
  const { cohortId } = useActiveCohort();
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(100);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await fetch(withCohort(`${BASE}api/investor-portal/research/${pimId}/document`, cohortId), {
          headers: authHeaders(),
          credentials: "include",
          cache: "no-store",
        });
        if (!response.ok) throw new Error(await errorMessage(response, "The document could not be loaded."));
        const url = URL.createObjectURL(await response.blob());
        if (cancelled) URL.revokeObjectURL(url);
        else {
          objectUrlRef.current = url;
          setDocumentUrl(`${url}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "The document could not be loaded.");
      }
    };
    void load();
    return () => {
      cancelled = true;
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, [cohortId, pimId]);

  const scale = zoom / 100;
  return (
    <div className="rr-scope fixed inset-0 z-[100] flex h-[100dvh] flex-col" data-testid="research-reading-room">
      <header className="rr-bar flex min-h-14 shrink-0 items-center justify-between gap-3 border-b px-3 md:min-h-16 md:px-8">
        <button data-testid="button-back-to-research" onClick={() => navigate(`/investor/portal/research/${pimId}`)} className="rr-btn flex min-h-10 items-center gap-2 rounded-md px-2 text-sm font-medium focus-visible:outline-2 focus-visible:outline-[#FF7A2E]">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to research
        </button>
        <div className="hidden items-center gap-2 sm:flex">
          <span className="rr-mark grid h-7 w-7 place-items-center rounded font-serif font-bold">F</span>
          <div><b className="rr-ink block text-sm">Favcy Select</b><span className="rr-muted font-mono text-[10px] tracking-wider">READING ROOM</span></div>
        </div>
        <button data-testid="button-close-reading-room" aria-label="Close Reading Room" onClick={() => navigate(`/investor/portal/research/${pimId}`)} className="rr-btn grid h-10 w-10 place-items-center rounded-md focus-visible:outline-2 focus-visible:outline-[#FF7A2E]">
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      </header>
      <main className="min-h-0 flex-1 overflow-auto px-2 py-3 md:px-8 md:py-6">
        {error ? <div className="mx-auto max-w-xl"><Failure message={error} retry={() => window.location.reload()} /></div> : !documentUrl ? (
          <div className="rr-muted"><Status>Preparing the private document…</Status></div>
        ) : (
          <div
            className="rr-page mx-auto shadow-2xl"
            style={{ width: `calc(${scale} * min(100%, 1024px))`, minWidth: zoom > 100 ? `calc(${scale} * min(100%, 1024px))` : undefined }}
          >
            <iframe
              data-testid="document-research-pdf"
              title="Server-watermarked private research memorandum"
              src={documentUrl}
              className="block w-full border-0"
              style={{ height: `max(480px, calc((100dvh - 9.5rem) * ${Math.max(1, scale)}))` }}
            />
          </div>
        )}
      </main>
      <footer className="rr-bar flex shrink-0 flex-col items-center gap-2 border-t px-3 py-2 md:flex-row md:justify-between md:px-8">
        <p className="rr-muted order-2 max-w-md text-center text-[11px] leading-4 md:order-1 md:text-left">
          Controlled inline access deters casual redistribution but cannot prevent screenshots or determined browser-level capture.
        </p>
        <div role="toolbar" aria-label="Document zoom" className="order-1 flex items-center gap-1 md:order-2">
          <FileText className="rr-muted mx-2 h-4 w-4" aria-hidden="true" />
          <span className="rr-divider mx-1 h-5 w-px" />
          <button data-testid="button-zoom-out" aria-label="Zoom out" disabled={zoom <= 70} onClick={() => setZoom(value => Math.max(70, value - 10))} className="rr-btn grid h-10 w-10 place-items-center rounded-md"><Minus className="h-4 w-4" /></button>
          <span data-testid="text-document-zoom" aria-live="polite" className="rr-ink min-w-12 text-center font-mono text-xs">{zoom}%</span>
          <button data-testid="button-zoom-in" aria-label="Zoom in" disabled={zoom >= 140} onClick={() => setZoom(value => Math.min(140, value + 10))} className="rr-btn grid h-10 w-10 place-items-center rounded-md"><Plus className="h-4 w-4" /></button>
          <button data-testid="button-fit-document" aria-label="Fit document to width" onClick={() => setZoom(100)} className="rr-btn grid h-10 w-10 place-items-center rounded-md"><RotateCcw className="h-4 w-4" /></button>
        </div>
      </footer>
    </div>
  );
}

export function ResearchDashboardFeature() {
  const { cohortId } = useActiveCohort();
  const [items, setItems] = useState<ResearchIndexItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestSequence = useRef(0);

  const load = useCallback(async () => {
    const request = ++requestSequence.current;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(withCohort(`${BASE}api/investor-portal/research`, cohortId), {
        headers: authHeaders(),
        credentials: "include",
        cache: "no-store",
      });
      if (!response.ok) throw new Error(await errorMessage(response));
      const result = unwrapResearchIndex(await response.json() as ResearchIndexEnvelope);
      if (request === requestSequence.current) setItems(result.items);
    } catch (err) {
      if (request === requestSequence.current) {
        setError(err instanceof Error ? err.message : "Research could not be loaded.");
      }
    } finally {
      if (request === requestSequence.current) setLoading(false);
    }
  }, [cohortId]);

  useEffect(() => {
    void load();
    const refreshOnFocus = () => { void load(); };
    window.addEventListener("focus", refreshOnFocus);
    return () => {
      requestSequence.current += 1;
      window.removeEventListener("focus", refreshOnFocus);
    };
  }, [load]);

  const statusDetails = {
    new: { label: "New" },
    in_progress: { label: "In progress" },
    completed: { label: "Completed" },
  } as const;

  return (
    <section
      aria-labelledby="dashboard-research-title"
      data-testid="section-research-library"
      className="min-w-0 overflow-hidden rounded-2xl border border-border bg-card text-card-foreground"
    >
      <div className="flex flex-col gap-5 border-b border-border p-6 sm:flex-row sm:items-end sm:justify-between md:p-7">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.16em] text-primary">
            <BookOpen className="h-4 w-4" aria-hidden="true" /> Research Pipeline
          </div>
          <h2 id="dashboard-research-title" className="mt-3 font-serif text-3xl">Your research library</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Follow every memorandum available to you and continue from where you left off.
          </p>
          <p className="mt-2 text-xs leading-5 text-muted-foreground">
            Preliminary evaluations with company claims, reviewed evidence, open gaps, risks and the private PDF. Not an investment recommendation or portfolio admission.
          </p>
        </div>
        <Link
          href="/investor/portal/research"
          data-testid="link-view-all-research"
          className="inline-flex shrink-0 items-center gap-2 text-xs font-semibold text-primary hover:underline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
        >
          All available research <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>

      {loading ? (
        <div role="status" data-testid="status-dashboard-research-loading" className="flex items-center justify-center gap-3 px-6 py-12 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin text-primary" aria-hidden="true" />
          <span className="text-sm">Refreshing your research library…</span>
        </div>
      ) : error ? (
        <div role="alert" data-testid="status-dashboard-research-error" className="research-error-surface m-6 min-w-0 break-words rounded-lg border p-5">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <div>
              <p className="text-sm">{error}</p>
              <button
                data-testid="button-retry-dashboard-research"
                onClick={() => { void load(); }}
                className="mt-3 text-xs font-semibold underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-current"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      ) : items.length === 0 ? (
        <div data-testid="status-dashboard-research-empty" className="px-6 py-12 text-center md:px-7">
          <p className="font-serif text-xl">No research is available yet.</p>
          <p className="mt-2 text-sm text-muted-foreground">Newly granted research will appear here after your library refreshes.</p>
        </div>
      ) : (
        <div className="rl-scope grid gap-5 p-5 sm:grid-cols-2 md:p-6 xl:grid-cols-3" data-testid="list-dashboard-research">
          {items.map(item => {
            const status = statusDetails[item.readingStatus] ?? statusDetails.new;
            const progress = Math.min(100, Math.max(0, item.readingProgressPercent));
            const readingHref = item.readingStatus === "new"
              ? `/investor/portal/research/${item.id}`
              : `/investor/portal/research/${item.id}/read`;
            return (
              <article key={item.id} data-testid={`card-dashboard-research-${item.id}`} className="rl-card flex min-w-0 flex-col overflow-hidden rounded-2xl border">
                <Link href={`/investor/portal/research/${item.id}`} tabIndex={-1} aria-hidden="true" className="block">
                  <ResearchCover seed={item.slug} companyName={item.companyName} sector={item.sector} />
                </Link>
                <div className="flex flex-1 flex-col p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span data-testid={`status-reading-${item.id}`} data-status={item.readingStatus}
                      className="rl-badge rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider">
                      {status.label}
                    </span>
                    <span data-testid={`text-reading-progress-${item.id}`} className="rl-muted font-mono text-[10px]">{progress}% read</span>
                  </div>
                  <Link href={`/investor/portal/research/${item.id}`} data-testid={`link-dashboard-research-detail-${item.id}`}
                    className="rl-ink mt-4 font-serif text-xl hover:underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-[#C94A00]">
                    {item.companyName}
                  </Link>
                  <p className="rl-body mt-2 line-clamp-2 text-sm leading-6">{item.cardSnippet}</p>
                  <p className="rl-muted mt-3 font-mono text-[10px] uppercase tracking-wider">
                    {[item.evaluationStatus, item.geography, item.estimatedReadMinutes ? `${item.estimatedReadMinutes} min read` : null].filter(Boolean).join(" · ")}
                  </p>
                  <div role="progressbar" aria-label={`${item.companyName} reading progress`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}
                    className="rl-track mt-auto h-1 overflow-hidden rounded-full" style={{ marginTop: "1.25rem" }}>
                    <div className="rl-fill h-full transition-[width]" style={{ width: `${progress}%` }} />
                  </div>
                  <Link href={readingHref} data-testid={`link-dashboard-research-read-${item.id}`}
                    className="rl-link mt-4 inline-flex items-center gap-2 self-start text-xs font-semibold focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#C94A00]">
                    {item.readingStatus === "new" ? "View research" : item.readingStatus === "completed" ? "Read again" : "Continue reading"}
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}